import { useState, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

interface CollectionModeState {
  active: boolean;
  collectionId: string | null;
  collectionName: string | null;
  collectionColor: string | null;
}

const INITIAL_STATE: CollectionModeState = {
  active: false,
  collectionId: null,
  collectionName: null,
  collectionColor: null,
};

export interface UseCollectionModeReturn {
  state: CollectionModeState;
  locationIds: Set<string>;
  enter: (collection: { id: string; name: string; color: string }) => void;
  exit: () => void;
  toggle: (locationId: string) => void;
  isInCollection: (locationId: string) => boolean;
}

export function useCollectionMode(): UseCollectionModeReturn {
  const [state, setState] = useState<CollectionModeState>(INITIAL_STATE);
  const [locationIds, setLocationIds] = useState<Set<string>>(new Set());

  const utils = trpc.useUtils();

  const addMutation = trpc.collections.addLocations.useMutation();
  const removeMutation = trpc.collections.removeLocations.useMutation();

  const enter = useCallback(
    (collection: { id: string; name: string; color: string }) => {
      setState({
        active: true,
        collectionId: collection.id,
        collectionName: collection.name,
        collectionColor: collection.color,
      });

      // Seed the local set with existing collection members
      utils.collections.getMapData
        .fetch({ collectionId: collection.id })
        .then((data) => {
          const ids = new Set((data ?? []).map((loc: any) => loc.id as string));
          setLocationIds(ids);
        })
        .catch(() => {
          // Start with empty set on error
          setLocationIds(new Set());
        });
    },
    [utils],
  );

  const exit = useCallback(() => {
    setState(INITIAL_STATE);
    setLocationIds(new Set());
    utils.collections.list.invalidate();
    utils.collections.getById.invalidate();
  }, [utils]);

  const toggle = useCallback(
    (locationId: string) => {
      if (!state.collectionId) return;

      const collectionId = state.collectionId;
      const wasInCollection = locationIds.has(locationId);

      // Optimistic update — create a new Set for React reactivity
      setLocationIds((prev) => {
        const next = new Set(prev);
        if (wasInCollection) {
          next.delete(locationId);
        } else {
          next.add(locationId);
        }
        return next;
      });

      const mutation = wasInCollection ? removeMutation : addMutation;
      mutation.mutate(
        { collectionId, locationIds: [locationId] },
        {
          onError: () => {
            // Revert optimistic update
            setLocationIds((prev) => {
              const reverted = new Set(prev);
              if (wasInCollection) {
                reverted.add(locationId);
              } else {
                reverted.delete(locationId);
              }
              return reverted;
            });
            toast.error(
              wasInCollection
                ? "Failed to remove from collection"
                : "Failed to add to collection",
            );
          },
        },
      );
    },
    [state.collectionId, locationIds, addMutation, removeMutation],
  );

  const isInCollection = useCallback(
    (locationId: string) => locationIds.has(locationId),
    [locationIds],
  );

  return { state, locationIds, enter, exit, toggle, isInCollection };
}
